import { ArticleDatabaseCatalog } from '../articles/article_database_catalog.js?v=20260831f';
import { isFallbackBomArticleNo } from './bom_article_status.js?v=20260706d';
import { S } from '../app/app_state.js?v=20260817c';

function makeBomManualId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `manual-${crypto.randomUUID()}`;
  }
  return `manual-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeManualQuantity(value, fallback = 1) {
  const normalizedText = String(value ?? '').trim().replace(',', '.');
  const numeric = Number(normalizedText);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    const fallbackNumeric = Number(fallback);
    return Number.isFinite(fallbackNumeric) && fallbackNumeric > 0 ? Math.max(1, Math.round(fallbackNumeric)) : 1;
  }
  return Math.max(1, Math.round(numeric));
}

function normalizeManualDescription(...values) {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text) return text;
  }
  return '';
}

function ensureBomOverrides(project = S.project) {
  if (!project.bomOverrides || typeof project.bomOverrides !== 'object' || Array.isArray(project.bomOverrides)) {
    project.bomOverrides = {};
  }
  if (!Array.isArray(project.bomOverrides.manualItems)) project.bomOverrides.manualItems = [];
  if (!Array.isArray(project.bomOverrides.order)) project.bomOverrides.order = [];
  project.bomOverrides.manualItems = project.bomOverrides.manualItems
    .map(normalizeManualItem)
    .filter(Boolean);
  project.bomOverrides.order = project.bomOverrides.order
    .map(value => String(value || '').trim())
    .filter(Boolean);
  return project.bomOverrides;
}

function normalizeManualItem(item = {}) {
  const id = String(item.id || '').trim() || makeBomManualId();
  const articleNo = ArticleDatabaseCatalog.normalizeArticleNo(item.articleNo || item.ART_NRA || item.number || '');
  if (!articleNo) return null;
  const quantity = normalizeManualQuantity(item.quantity ?? item.menge, 1);
  const bomTextAdditions = normalizeBomOverrideTextAdditions(item.bomTextAdditions);
  const description1 = normalizeManualDescription(
    item.beschreibung1,
    item.description1,
    item.technischebezeichnung,
    item.technicalDescription,
    item.name,
    item.label,
    item.description,
    item.suchbegriff,
    item.searchTerm
  );
  const description2 = normalizeManualDescription(
    item.beschreibung2,
    item.description2,
    item.subtitle,
    item.detailText,
    item.detail,
    item.note
  );
  return {
    id,
    articleNo,
    number: articleNo,
    quantity,
    menge: quantity,
    source: 'manual',
    isManual: true,
    ...(hasBomOverrideTextAdditions(bomTextAdditions) ? { bomTextAdditions } : {}),
    ...(description1 ? { beschreibung1: description1, description1 } : {}),
    ...(description2 ? { beschreibung2: description2, description2 } : {}),
    bomGroupId: String(item.bomGroupId || '').trim(),
    bomGroupName: String(item.bomGroupName || '').trim()
  };
}

function manualRowKey(itemOrId) {
  const id = typeof itemOrId === 'string' ? itemOrId : itemOrId?.id;
  return id ? `manual:${id}` : '';
}

function drawingRowKey(row = {}) {
  return [
    'drawing',
    row.bomGroupId || '',
    row.articleNo || row.number || '',
    row.articleKey || row.unknownArticleKey || '',
    bomTextAdditionsKey(row.bomTextAdditions),
    row.sourceSystemId || row.systemKey || '',
    row.unit || ''
  ].map(value => String(value ?? '')).join(':');
}

function bomTextAdditionsKey(additions = {}) {
  const normalized = normalizeBomOverrideTextAdditions(additions);
  return hasBomOverrideTextAdditions(normalized) ? JSON.stringify(normalized) : '';
}

function normalizeBomOverrideTextAdditions(additions = {}) {
  const source = additions && typeof additions === 'object' && !Array.isArray(additions)
    ? additions
    : {};
  return {
    description1Suffixes: normalizeBomOverrideTextAdditionList(
      source.description1Suffix,
      source.description1Suffixes,
      source.description1Addition,
      source.description1Additions
    ),
    description2Suffixes: normalizeBomOverrideTextAdditionList(
      source.description2Suffix,
      source.description2Suffixes,
      source.description2Addition,
      source.description2Additions
    )
  };
}

function normalizeBomOverrideTextAdditionList(...values) {
  const result = [];
  const visit = value => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    const text = String(value || '').trim();
    if (text && !result.includes(text)) result.push(text);
  };
  values.forEach(visit);
  return result;
}

function hasBomOverrideTextAdditions(additions = {}) {
  return (additions.description1Suffixes || []).length > 0
    || (additions.description2Suffixes || []).length > 0;
}

function rowKey(row = {}) {
  return row.isManual ? manualRowKey(row.manualId || row.id) : drawingRowKey(row);
}

function getManualItems(project = S.project) {
  return ensureBomOverrides(project).manualItems.slice();
}

function createManualItem(articleNo, options = {}) {
  const quantity = normalizeManualQuantity(options.quantity, 1);
  return normalizeManualItem({
    id: makeBomManualId(),
    articleNo,
    quantity,
    menge: quantity,
    bomGroupId: options.bomGroupId || '',
    bomGroupName: options.bomGroupName || ''
  });
}

function addManualItem(item, options = {}) {
  const overrides = ensureBomOverrides(options.project || S.project);
  const normalized = normalizeManualItem(item);
  if (!normalized) return null;
  overrides.manualItems.push(normalized);
  const key = manualRowKey(normalized);
  const order = Array.isArray(options.visibleOrder) ? options.visibleOrder.filter(Boolean) : overrides.order.slice();
  const afterKey = String(options.afterKey || '').trim();
  const withoutNew = order.filter(value => value !== key);
  const index = afterKey ? withoutNew.indexOf(afterKey) : -1;
  if (index >= 0) withoutNew.splice(index + 1, 0, key);
  else withoutNew.push(key);
  overrides.order = withoutNew;
  return normalized;
}

function saveOrder(rowKeys = [], options = {}) {
  const overrides = ensureBomOverrides(options.project || S.project);
  overrides.order = rowKeys.map(value => String(value || '').trim()).filter(Boolean);
}

function updateManualItemQuantity(itemId, quantity, options = {}) {
  const overrides = ensureBomOverrides(options.project || S.project);
  const id = String(itemId || '').trim();
  const item = overrides.manualItems.find(entry => entry.id === id);
  if (!item) return null;
  const nextQuantity = normalizeManualQuantity(quantity, item.quantity ?? item.menge ?? 1);
  item.quantity = nextQuantity;
  item.menge = nextQuantity;
  delete item.einzelgewicht;
  delete item.gewichtTotal;
  item.source = 'manual';
  item.isManual = true;
  return item;
}

function manualItemToRow(item = {}, options = {}) {
  const article = ArticleDatabaseCatalog.get(item.articleNo) || null;
  const isUnknownArticle = isFallbackBomArticleNo(item.articleNo) || !article;
  const articleNo = ArticleDatabaseCatalog.getArticleNo(article) || item.articleNo;
  const quantity = normalizeManualQuantity(item.quantity ?? item.menge, 1);
  const bomTextAdditions = normalizeBomOverrideTextAdditions(item.bomTextAdditions);
  const description1 = isUnknownArticle
    ? normalizeManualDescription(
      item.beschreibung1,
      item.description1,
      item.technischebezeichnung,
      item.technicalDescription,
      item.name,
      item.label,
      item.description,
      item.suchbegriff,
      item.searchTerm
    )
    : ArticleDatabaseCatalog.getArticleDescription1(article);
  const description2 = isUnknownArticle
    ? normalizeManualDescription(
      item.beschreibung2,
      item.description2,
      item.subtitle,
      item.detailText,
      item.detail,
      item.note
    )
    : ArticleDatabaseCatalog.getArticleDescription2(article);
  return {
    id: item.id,
    manualId: item.id,
    bomRowKey: manualRowKey(item),
    articleNo,
    number: articleNo,
    suchbegriff: isUnknownArticle ? '' : ArticleDatabaseCatalog.getArticleSearchTerm(article),
    searchTerm: isUnknownArticle ? '' : ArticleDatabaseCatalog.getArticleSearchTerm(article),
    beschreibung1: description1,
    description1,
    description1Additions: bomTextAdditions.description1Suffixes,
    beschreibung2: description2,
    description2,
    description2Additions: bomTextAdditions.description2Suffixes,
    ...(hasBomOverrideTextAdditions(bomTextAdditions) ? { bomTextAdditions } : {}),
    quantity,
    menge: quantity,
    unit: isUnknownArticle ? '' : (ArticleDatabaseCatalog.getArticleUnit(article) || 'Stk'),
    priceChf: isUnknownArticle ? '' : ArticleDatabaseCatalog.getArticlePriceChf(article),
    vpChf: isUnknownArticle ? '' : ArticleDatabaseCatalog.getArticleSalePriceChf(article),
    currency: article?.currency || 'CHF',
    weight: isUnknownArticle ? '' : ArticleDatabaseCatalog.getArticleWeightKg(article),
    weightKg: isUnknownArticle ? '' : ArticleDatabaseCatalog.getArticleWeightKg(article),
    weightUnit: isUnknownArticle ? '' : (article.weightUnit || 'kg'),
    source: 'manual',
    isManual: true,
    isUnknownArticle,
    unknownArticleReason: isUnknownArticle ? 'Manueller Artikel ist nicht im Artikelkatalog referenziert.' : '',
    bomGroupId: options.groupMode === 'none' ? '' : (item.bomGroupId || ''),
    bomGroupName: options.groupMode === 'none' ? '' : (item.bomGroupName || '')
  };
}

function applyBomOverrides(rows = [], options = {}) {
  const overrides = ensureBomOverrides(options.project || S.project);
  const drawingRows = (rows || []).map(row => {
    const key = drawingRowKey(row);
    return {
      ...row,
      bomRowKey: key,
      source: row.source || 'drawing',
      isManual: false
    };
  });
  const manualRows = overrides.manualItems.map(item => manualItemToRow(item, options));
  const merged = drawingRows.concat(manualRows);
  const orderIndex = new Map(overrides.order.map((key, index) => [key, index]));
  return merged
    .map((row, index) => ({ row, index, key: row.bomRowKey || rowKey(row) }))
    .sort((a, b) => {
      const ai = orderIndex.has(a.key) ? orderIndex.get(a.key) : Number.MAX_SAFE_INTEGER;
      const bi = orderIndex.has(b.key) ? orderIndex.get(b.key) : Number.MAX_SAFE_INTEGER;
      return ai - bi || a.index - b.index;
    })
    .map(entry => entry.row);
}

export {
  ensureBomOverrides,
  getManualItems,
  createManualItem,
  addManualItem,
  applyBomOverrides,
  saveOrder,
  updateManualItemQuantity,
  normalizeManualQuantity,
  rowKey,
  drawingRowKey,
  manualRowKey
};
